import { IDesignerState } from '@/../typings';
import { filestore, FsFile, FsImage } from '@/filestore';
import { JUTE_VERSION, DEFAULT_LAUNCH_SCREEN, ENVIRONMENT, FILESTORE_URL } from '@/settings';
import { store } from '@/store';
import { finaliseContentDoc } from '@/utils/pure';
import { IActionCategory, IActionDict, IComponentType, IContent360Scene, IContentDoc, ISpatialComponentUnion } from '@r3f-component-data-structure';
import { ILaunchScreen } from '@r3f-component-data-structure/types/launchScreen';
import { zip, AsyncZippable } from 'fflate';
import { convertStringArrayToITuple4 } from '../../menus/InspectorMenu/ProjectShelves/LaunchScreen/utils';

interface IFileForBundler {
    path: string
    bin?: Uint8Array
}

export async function bundleProject() {
    const tStart = performance.now();
    const doc: IContentDoc = finaliseContentDoc((store.getState() as IDesignerState).contentReducer.contentDoc)
    // Download all media files, referenced in the doc, that will be added to the zip
    const mediaFilesPromises = _getMediaFiles(doc)

    const launchScreen = doc.launchScreen || DEFAULT_LAUNCH_SCREEN;
    let lsRootUrl = `https://media.zap.works/launch-screen/${launchScreen.id}`;
    if (ENVIRONMENT !== 'production') {
        lsRootUrl = launchScreen.id !== 'lite-branded' ? `https://media.zap.works/launch-screen-stg/${launchScreen.id}` : `https://media.zap.works/launch-screen-stg/default`;
    }

    const lsManifest = await (await fetch(`${lsRootUrl}/.zw-manifest.json`)).json()
    const launchScreenPromises: Promise<IFileForBundler[]>[] = []
    for (const path of lsManifest.files) {
        // eslint-disable-next-line no-async-promise-executor
        launchScreenPromises.push(new Promise(async resolve => {
            const res = await fetch(`${lsRootUrl}/${path}`)
            if (path === 'index.html') {
                const indexFiles = await _renderLaunchScreenIndex(await res.text(), launchScreen)
                resolve(indexFiles)
            } else {
                resolve([{ path, bin: new Uint8Array(await res.arrayBuffer()) }])
            }
        }))
    }

    const filesArray = await Promise.all([mediaFilesPromises, ...launchScreenPromises])
    // Media binaries under fs/ are already compressed (jpg/png/glb/mp3/zpt/img.hq) — store them
    // without deflate via the per-file tuple form. Everything else (content.json, launch-screen
    // html/js/css) uses fflate's default level.
    const filesToZip: AsyncZippable = {}
    for (const files of filesArray) {
        for (const fl of files) {
            if (typeof fl.bin === 'undefined') continue
            filesToZip[fl.path] = fl.path.startsWith('fs/') ? [fl.bin, { level: 0 }] : fl.bin
        }
    }
    const projectId = (store.getState() as IDesignerState).userReducer.project.id
    // Anything the CDS marked public is streamed from the filestore rather than packed into the zip
    // (FsVideo returns bundler entries with no `bin`), so its id has to become an absolute URL.
    // The prefix alone is the marker: `importFromZmlId` mints `public-zml-<uuid>`, the doc backfill's
    // `importFromZmlCdnUrl` mints `public-<wizcontentId>`, and a direct upload mints `public-<uuid>`.
    // Matching only `public-zml-` left the latter two as bare ids in the published content.json.
    const toFullUrl = (id: string) => id.startsWith('public-') ? `${FILESTORE_URL}/proj-${projectId}/${id}` : id

    const remappedMediaFilesById: typeof doc.mediaFilesById = {}
    for (const [key, value] of Object.entries(doc.mediaFilesById)) {
        remappedMediaFilesById[toFullUrl(key)] = { ...value, id: toFullUrl(value.id) }
    }
    const docToBundle = { ...doc, mediaFilesById: remappedMediaFilesById }

    const docJson = JSON.stringify(docToBundle, (key, value) => {
        if (key === 'filestoreId' && typeof value === 'string') return toFullUrl(value)
        return value
    })
    filesToZip['content.json'] = (new TextEncoder()).encode(docJson)

    console.log('Bundling took', performance.now() - tStart, 'ms')
    return new Promise<Blob>((resolve, reject) => {
        zip(filesToZip, (err, zipFile) => {
            if (err) return reject(err)
            const file = new File([zipFile as unknown as ArrayBuffer], 'project.zip', { type: 'application/zip' })
            resolve(file)
        })
    })
}

async function _getMediaFiles(doc: IContentDoc) {
    const files: FsFile[] = []
    for (const comp of Object.values(doc.componentsById)) {
        if (comp.type === IComponentType.Image || comp.type === IComponentType.Model3d || comp.type === IComponentType.Video) {
            files.push(filestore.load(comp.filestoreId))
        } else if (comp.type === IComponentType.Scene) {
            const content360Scene = comp as IContent360Scene
            if (content360Scene.content360) {
                files.push(filestore.load(content360Scene.content360.filestoreId))
            }
        }
        if (comp.type === IComponentType.Root && typeof comp.bkgrSound?.filestoreId !== 'undefined') {
            files.push(filestore.load(comp.bkgrSound.filestoreId))
        }
        files.push(..._getActionsMediaFiles((comp as ISpatialComponentUnion).actions)) // Scene, Button, Image, etc.
    }
    if (typeof doc.tracking?.image !== 'undefined') {
        const trackingImageDict = doc.tracking!.image;
        for (const trackingData of Object.values(trackingImageDict)) {
            files.push(filestore.load(trackingData.filestoreId))
        }
    }

    // Dedupe by id: filestore.load() returns the same FsFile instance per id, but multiple
    // references would still each fire their own fetch (the blob cache stores resolved values,
    // not in-flight promises) — one entry per id collapses that to a single download.
    const uniqueFiles = Array.from(new Map(files.map(f => [f.id, f])).values())
    const promises: Promise<IFileForBundler[]>[] = uniqueFiles.map(fsFile => fsFile.getFilesForBundler())
    const filesForBundler = await Promise.all(promises)
    return filesForBundler.flat()
}

function _getActionsMediaFiles(actionsByTrigger?: IActionDict) {
    const files: FsFile[] = []
    if (typeof actionsByTrigger === 'undefined') return files
    for (const [_trigger, actions] of Object.entries(actionsByTrigger)) {
        for (const action of actions) {
            if (action.type === IActionCategory.playSound) {
                files.push(filestore.load(action.filestoreId))
            }
        }
    }
    return files
}

async function _renderLaunchScreenIndex(indexHtml: string, launchScreen: ILaunchScreen) {
    indexHtml = indexHtml.replace('</body>', `<script src="https://libs.zappar.com/jute/${JUTE_VERSION}/index.js"></script></body>`)
    // indexHtml = indexHtml.replace(new RegExp('{{ JUTE_VERSION }}', 'g'), JUTE_VERSION)
    indexHtml = indexHtml.replace(new RegExp('{{ TITLE }}', 'g'), launchScreen.fields?.title?.value || 'An Augmented Reality Experience')

    const iconFilestoreId = launchScreen?.fields?.iconFilestoreId?.value;
    let iconUrl = './icon.svg';

    let fsImage: FsImage | undefined;

    if (iconFilestoreId) {
        fsImage = filestore.load<FsImage>(iconFilestoreId);
        iconUrl = `./${fsImage.getBundlerPath()}`;
    }    

    indexHtml = indexHtml.replace(new RegExp('{{ ICON_URL }}', 'g'), iconUrl)
    
    indexHtml = indexHtml.replace(new RegExp('{{ BUTTON_TEXT }}', 'g'), launchScreen.fields?.buttonText?.value || 'Launch')
    indexHtml = indexHtml.replace(new RegExp('{{ ICON_SIZE }}', 'g'), launchScreen.fields?.iconSize?.value || 'medium')
    
    let bgColor = launchScreen.fields?.backgroundColorRGBA?.value || '[255,255,255,1]'
    let elementColor = launchScreen.fields?.elementColorRGBA?.value || '[7,49,88,1]'
    if (bgColor.startsWith('[') || elementColor.startsWith('[')) {
        bgColor = `${convertStringArrayToITuple4(bgColor)}`
        elementColor =`${convertStringArrayToITuple4(elementColor)}`
    }
    indexHtml = indexHtml.replace(new RegExp('{{ BACKGROUND_COLOR }}', 'g'), bgColor || '')
    indexHtml = indexHtml.replace(new RegExp('{{ ELEMENT_COLOR }}', 'g'), elementColor || '')
    
    indexHtml = indexHtml.replace(new RegExp('{{ HIDDEN_CUSTOM_ICON }}', 'g'), iconFilestoreId ? '' : 'hidden')
    indexHtml = indexHtml.replace(new RegExp('{{ HIDDEN_DEFAULT_ICON }}', 'g'), iconFilestoreId ? 'hidden' : '')
    const encoder = new TextEncoder()
    const res = [{ path: 'index.html', bin: (encoder.encode(indexHtml) as unknown as Uint8Array<ArrayBuffer | ArrayBufferLike>) }]
    if (fsImage) {
        res.push(...(await fsImage.getFilesForBundler()))
    }
    return res
}
